// * Layouts Component
import {
    Alert,
    Box,
    Backdrop,
    CircularProgress,
    Fade,
    IconButton,
    Stack
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { useAuthenticator } from "@aws-amplify/ui-react"
import { useAtom } from "jotai"
import { useEffect } from "react"
import { lambdaInvoke } from "../../services/lambda"
import { emptyStringObject } from "../../utils"
import { uiControlAtom, userAtom, qnaUrlAtom } from "../../states"
import Header from "./Header"
import Sidebar from "./Sidebar"

const QuickSightEmbedding = require("amazon-quicksight-embedding-sdk")

// Simple localStorage polyfill for QuickSight embedding
if (typeof Storage === "undefined") {
    (window as any).localStorage = {
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {},
        clear: () => {}
    }
}

const Layouts = () => {
    const { authStatus, user: amplifyUser } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ])

    const [uiControl, setUiControl] = useAtom(uiControlAtom)
    const [, setUser] = useAtom(userAtom)
    const [qnaUrl, setQnaUrl] = useAtom(qnaUrlAtom)

    const handleErrorClose = () => {
        setUiControl({
            ...uiControl,
            error: ""
        })
    }

    useEffect(() => {
        if (!emptyStringObject(uiControl.incomingDashboard)) {
            const handleDashboardChange = async (
                dashboardId: string,
                dashboardMode: string
            ) => {
                const id_token =
                    amplifyUser
                        .getSignInUserSession()
                        ?.getIdToken()
                        .getJwtToken() || ""

                try {
                    // * Shows loader
                    setUiControl({
                        ...uiControl,
                        loading: true
                    })

                    const data = await lambdaInvoke({
                        id_token,
                        dashboardId,
                        dashboardMode
                    })

                    if (data) {
                        const { user, url, qUrl } = data

                        setUser(user)
                        
                        // Store the Q&A URL if it exists
                        console.log('Setting Q&A URL:', qUrl)
                        if (qUrl) {
                            setQnaUrl(qUrl)
                            console.log('Q&A URL set successfully')
                        } else {
                            console.log('No Q&A URL received from lambda')
                        }

                        setUiControl({
                            ...uiControl,
                            menuOpened: false,
                            currentDashboard: {
                                id: dashboardId,
                                url
                            },
                            incomingDashboard: {
                                id: "",
                                mode: ""
                            }
                        })

                        return
                    }

                    throw new Error("Empty Data!")
                } catch (error) {
                    setUiControl({
                        ...uiControl,
                        incomingDashboard: {
                            id: "",
                            mode: ""
                        },
                        loading: false,
                        error: "Sorry we were unable to load the dashboards"
                    })
                }
            }

            handleDashboardChange(
                uiControl.incomingDashboard.id,
                uiControl.incomingDashboard.mode
            )
        }
    }, [uiControl.incomingDashboard])

    useEffect(() => {
        if (!emptyStringObject(uiControl.currentDashboard)) {
            const embedDashboard = async () => {
                const container = document.getElementById(
                    "quicksight-embeded"
                ) as HTMLElement

                if (container.childElementCount !== 0) {
                    // * Clears children
                    container.innerHTML = ""
                }

                try {
                    // Create embedding context (it's async)
                    const embeddingContext = await QuickSightEmbedding.createEmbeddingContext()
                    console.log("Embedding context:", embeddingContext)

                    const dashboardIframe = await embeddingContext.embedDashboard({
                        url: uiControl.currentDashboard.url,
                        container
                    })

                    console.log("Dashboard iframe object:", dashboardIframe)
                    console.log("Dashboard iframe type:", typeof dashboardIframe)
                    console.log("Dashboard iframe 'on' method:", dashboardIframe?.on)

                    // Check if dashboardIframe has the 'addEventListener' method
                    if (dashboardIframe && typeof dashboardIframe.addEventListener === 'function') {
                        // * When dashboard has error
                        dashboardIframe.addEventListener("error", (payload: any) => {
                            console.error("Dashboard error:", payload)
                            setUiControl({
                                ...uiControl,
                                error: "Sorry we encountered an error. Please navigate to other pages or refresh this page to continue."
                            })
                        })

                        // * Triggered when dashboard fully loaded
                        dashboardIframe.addEventListener("load", (payload: any) => {
                            console.log("Dashboard loaded:", payload)
                            setUiControl({
                                ...uiControl,
                                error: "",
                                loading: false
                            })
                        })
                    } else {
                        console.warn("Dashboard iframe does not have 'addEventListener' method, using fallback")
                        // Fallback - just hide loading after a delay
                        setTimeout(() => {
                            setUiControl({
                                ...uiControl,
                                error: "",
                                loading: false
                            })
                        }, 2000)
                    }
                } catch (error) {
                    console.error("Error creating embedding context:", error)
                    setUiControl({
                        ...uiControl,
                        error: "Failed to initialize QuickSight embedding"
                    })
                }
            }

            embedDashboard()
        }
    }, [uiControl.currentDashboard])
    // Q&A embedding useEffect
    useEffect(() => {
        console.log("Q&A useEffect triggered, qnaUrl:", qnaUrl)
        if (qnaUrl) {
            const embedQnA = async () => {
                const qnaContainer = document.getElementById("qna-search-container") as HTMLElement
                console.log("Q&A container found:", qnaContainer)

                if (qnaContainer && qnaContainer.childElementCount === 0) {
                    console.log("Attempting to embed Q&A search bar")
                    try {
                        // Create embedding context for Q&A (it's async)
                        const embeddingContext = await QuickSightEmbedding.createEmbeddingContext()
                        
                        // Use proper Q&A search bar configuration according to official docs
                        const frameOptions = {
                            url: qnaUrl,
                            container: qnaContainer,
                            height: "40px",
                            width: "100%"
                        }

                        const contentOptions = {
                            panelOptions: {
                                panelType: 'SEARCH_BAR',
                                showQIcon: true
                            },
                            allowTopicSelection: true,
                            showTopicName: true,
                            allowFullscreen: true,
                            showPinboard: true,
                            onMessage: async (messageEvent: any) => {
                                switch (messageEvent.eventName) {
                                    case 'Q_SEARCH_OPENED': {
                                        console.log("Q&A search panel expanded")
                                        break
                                    }
                                    case 'Q_SEARCH_FOCUSED': {
                                        console.log("Q&A search panel focused")
                                        break
                                    }
                                    case 'Q_SEARCH_CLOSED': {
                                        console.log("Q&A search panel collapsed")
                                        break
                                    }
                                    case 'Q_SEARCH_ENTERED_FULLSCREEN': {
                                        console.log("Q&A entered fullscreen mode")
                                        break
                                    }
                                    case 'Q_SEARCH_EXITED_FULLSCREEN': {
                                        console.log("Q&A exited fullscreen mode")
                                        break
                                    }
                                    case 'CONTENT_LOADED': {
                                        console.log("Q&A search bar loaded successfully")
                                        break
                                    }
                                    case 'ERROR_OCCURRED': {
                                        const errorCode = messageEvent.message?.errorCode
                                        if (errorCode === 'Q_NO_TOPICS_AVAILABLE') {
                                            console.warn('Q&A topics not configured in QuickSight. Please configure Amazon Q topics in your QuickSight account.')
                                            // Hide the Q&A container since it's not functional
                                            if (qnaContainer) {
                                                qnaContainer.style.display = 'none'
                                            }
                                        } else {
                                            console.error('Generative Q&A Error:', messageEvent)
                                        }
                                        break
                                    }
                                }
                            }
                        }

                        const generativeQnA = await embeddingContext.embedGenerativeQnA(frameOptions, contentOptions)
                        console.log("Generative Q&A object:", generativeQnA)
                    } catch (error) {
                        console.log('embedGenerativeQnA not available, using iframe fallback')
                        // Fallback to iframe if SDK function fails
                        const iframe = document.createElement('iframe')
                        iframe.src = qnaUrl
                        iframe.style.width = '100%'
                        iframe.style.height = '38px'
                        iframe.style.border = '1px solid #ddd'
                        iframe.style.borderRadius = '20px'
                        iframe.style.backgroundColor = '#ffffff'
                        iframe.setAttribute('frameBorder', '0')
                        iframe.setAttribute('scrolling', 'no')
                        iframe.allow = 'fullscreen'
                        
                        qnaContainer.appendChild(iframe)
                    }
                }
            }

            embedQnA()
        }
    }, [qnaUrl])

    const error = (
        <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{
                zIndex: (theme) => theme.zIndex.appBar + 1,
                width: "100%",
                marginTop: { xs: "56px", sm: "64px" }
            }}
        >
            <Fade in={true}>
                <Alert
                    severity="error"
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={handleErrorClose}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    {uiControl.error}
                </Alert>
            </Fade>
        </Stack>
    )

    const loader = (
        <Backdrop
            sx={{
                color: "info.main",
                zIndex: (theme) => theme.zIndex.drawer + 1
            }}
            open
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    )

    return (
        <Box sx={{ display: "flex" }}>
            {uiControl.error && <>{error}</>}
            {authStatus === "unauthenticated" && <>{loader}</>}
            {authStatus === "authenticated" && (
                <>
                    {uiControl.loading && <>{loader}</>}
                    <Header />
                    <Sidebar />
                    <Box
                        id="quicksight-embeded"
                        component="main"
                        sx={{
                            width: "100%",
                            height: "calc(100% - 64px)",
                            top: { xs: "56px", sm: "64px" },
                            position: "fixed"
                        }}
                    />
                </>
            )}
        </Box>
    )
}

export default Layouts
